import React, { useId, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { reloadValuesEqual, type ReloadChoice, type ReloadChoices, type ReloadMergePlan } from "@/lib/recordReloadMerge";

export interface RecordReloadReviewProps {
  plan: ReloadMergePlan;
  labels: Record<string, string>;
  choices: ReloadChoices;
  onChoice: (key: string, choice: ReloadChoice) => void;
  onApply: () => void;
  onCancel?: () => void;
  formatValue?: (value: unknown, key: string) => ReactNode;
}

export function RecordReloadReview({ plan, labels, choices, onChoice, onApply, onCancel, formatValue }: RecordReloadReviewProps) {
  const { i18n } = useTranslation();
  const ar = (i18n.resolvedLanguage || i18n.language || "en").startsWith("ar");
  const id = useId();
  const text = ar ? {
    title: "مراجعة أحدث التغييرات", detail: "ستبقى تعديلاتك غير المتعارضة. اختر القيمة التي تريد الاحتفاظ بها عندما يتغير الحقل نفسه.",
    mine: "تعديلاتك", latest: "أحدث قيمة محفوظة", remote: "تم تغيير القيمة المحفوظة",
    choose: "اختر القيمة التي تريد الاحتفاظ بها", apply: "تطبيق القيم المحددة", cancel: "رجوع",
    empty: "لا توجد تغييرات للمراجعة.", missing: "غير محدد", yes: "نعم", no: "لا",
  } : {
    title: "Review latest changes", detail: "Your non-conflicting edits will be kept. Choose which value to keep when the same field has changed.",
    mine: "Your edits", latest: "Latest saved value", remote: "Saved value changed",
    choose: "Choose which value to keep", apply: "Apply selected values", cancel: "Back",
    empty: "No changes to review.", missing: "Not set", yes: "Yes", no: "No",
  };
  const display = (value: unknown, key: string): ReactNode => {
    if (formatValue) return formatValue(value, key);
    if (value === undefined || value === null || value === "") return text.missing;
    if (typeof value === "boolean") return value ? text.yes : text.no;
    return typeof value === "object" ? JSON.stringify(value) : String(value);
  };
  const conflicts = new Set(plan.conflicts.map((conflict) => conflict.key));
  const unresolved = plan.conflicts.some(({ key }) =>
    !Object.prototype.hasOwnProperty.call(choices, key) || (choices[key] !== "mine" && choices[key] !== "latest"));

  return (
    <section aria-labelledby={`${id}-title`} dir={ar ? "rtl" : "ltr"} className="space-y-4 rounded-lg border p-4">
      <div>
        <h3 id={`${id}-title`} className="font-semibold">{text.title}</h3>
        <p className="text-sm text-muted-foreground">{text.detail}</p>
      </div>
      {plan.changedKeys.length === 0 && <p className="text-sm">{text.empty}</p>}
      <div className="space-y-3">
        {plan.changedKeys.map((key, index) => {
          const remoteChanged = !reloadValuesEqual(plan.base[key], plan.latest[key]);
          const conflict = conflicts.has(key);
          return (
            <fieldset key={key} className={`min-w-0 rounded-md border p-3 ${remoteChanged ? "border-amber-500 bg-amber-50/50 dark:bg-amber-950/20" : ""}`}>
              <legend className="px-1 text-sm font-medium">{labels[key] || key}</legend>
              {remoteChanged && <p className="mb-2 text-xs font-medium text-amber-800 dark:text-amber-300">{text.remote}</p>}
              {conflict && <p id={`${id}-${index}-help`} className="mb-2 text-sm">{text.choose}</p>}
              <div className="grid gap-3 sm:grid-cols-2">
                {(["mine", "latest"] as const).map((side) => (
                  <label key={side} className={`flex min-w-0 gap-2 rounded border p-2 ${conflict ? "cursor-pointer" : ""}`}>
                    {conflict && <input type="radio" name={`${id}-${index}`} value={side}
                      checked={choices[key] === side} onChange={() => onChoice(key, side)}
                      aria-describedby={`${id}-${index}-help`} className="mt-1 shrink-0" />}
                    <span className="min-w-0">
                      <span className="block text-xs text-muted-foreground">{text[side]}</span>
                      <span className="block whitespace-pre-wrap break-words text-sm">{display(plan[side][key], key)}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          );
        })}
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        {onCancel && <Button type="button" variant="outline" onClick={onCancel}>{text.cancel}</Button>}
        <Button type="button" disabled={unresolved} onClick={onApply}>{text.apply}</Button>
      </div>
    </section>
  );
}

export default RecordReloadReview;